import React, { useEffect } from 'react';
import {
	Component,
	docgenFullScreenEditFlowId,
	getParentHeightAndWidth,
	pageID,
} from '../../../common/componentUtils';
import { PAGE_WIDTH_THRESHOLD } from '../../../constants';
import { StyledTextareaAnnotation } from './TextAnnotationComponent.styled';

interface TextAnnotationComponentProps {
	component: Component;
	handleComponentUpdate: (components: Component[], nextState?: string) => void;
	setComponentsToEdit: (comp: Component[]) => void;
	componentsToEdit: Component[];
	textAnnotationZIndex: number;
	updateTextAnnotationZIndex: (newIndex: number) => void;
}

const TextAnnotationComponent = ({
	component,
	handleComponentUpdate = () => {},
	setComponentsToEdit = () => {},
	componentsToEdit,
	textAnnotationZIndex,
	updateTextAnnotationZIndex,
}: TextAnnotationComponentProps) => {
	const [isEditable, setIsEditable] = React.useState(!component.value);
	const containerRef = React.useRef(null);
	const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
	const componentValue = React.useRef(component.value);
	const [availableWidth, setAvailableWidth] = React.useState(0);
	const [totalCols, setTotalCols] = React.useState(15);
	const [totalRows, setTotalRows] = React.useState(1);

	const componentForEdit = componentsToEdit?.[0];

	const padding = 1;

	React.useEffect(() => {
		componentValue.current = component.value;
	}, [component.value]);

	React.useEffect(() => {
		if (componentForEdit?.id !== component.id) {
			if (!componentValue.current) {
				handleComponentDeletion();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [componentForEdit?.id, component]);

	function handleComponentDeletion() {
		handleComponentUpdate([component], 'delete');
		setComponentsToEdit([]);
	}

	React.useEffect(() => {
		const { parentWidth } = getParentHeightAndWidth(pageID(component));
		const calculatedWidth = parentWidth - component.x * parentWidth;
		setAvailableWidth(calculatedWidth);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [component.x, component.fontSize]);

	React.useEffect(() => {
		const { parentWidth, parentHeight } = getParentHeightAndWidth(pageID(component));
		if (textAreaRef.current) {
			// This resizes the component height
			textAreaRef.current.style.height = '0px';
			textAreaRef.current.style.height = textAreaRef.current.scrollHeight + 'px';
			if (componentForEdit?.id === component.id) {
				handleComponentUpdate([
					{
						...component,
						value: textAreaRef.current.value,
						width: textAreaRef.current.scrollWidth / parentWidth,
						height: textAreaRef.current.scrollHeight / parentHeight,
					},
				]);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		textAreaRef.current?.scrollWidth,
		textAreaRef.current?.scrollHeight,
		availableWidth,
		component.fontSize,
	]);

	function onUpdate(event) {
		const textarea = event.target;
		const colsAvailable = Math.max(...textarea.value.split('\n').map(str => str.length));

		setTotalCols(colsAvailable);
		setTotalRows(textarea.value.split('\n').length);

		const { parentWidth, parentHeight } = getParentHeightAndWidth(pageID(component));
		const factor =
			(textAreaRef.current?.scrollHeight! - (component.fontSize! + 4 * padding)) /
				(component.fontSize! + 2 * padding) +
			1;
		textAreaRef.current!.rows = factor;
		handleComponentUpdate([
			{
				...component,
				value: textarea.value,
				width: textAreaRef.current?.scrollWidth! / parentWidth,
				height: textAreaRef.current?.scrollHeight! / parentHeight,
			},
		]);
	}

	React.useEffect(() => {
		if (component.id === componentForEdit?.id && textAnnotationZIndex === 1) {
			setIsEditable(true);
		}
	}, [component.id, componentForEdit?.id, textAnnotationZIndex]);

	function handleBlur() {
		const { parentWidth, parentHeight } = getParentHeightAndWidth(pageID(component));
		if (componentForEdit?.id === component.id) {
			handleComponentUpdate([
				{
					...component,
					value: textAreaRef.current?.value,
					width: textAreaRef.current?.scrollWidth! / parentWidth,
					height: textAreaRef.current?.scrollHeight! / parentHeight,
				},
			]);
		}

		setIsEditable(!componentValue.current);
		updateTextAnnotationZIndex(0);
	}

	useEffect(() => {
		if (textAreaRef.current) {
			const annotationPosition = textAreaRef.current.getBoundingClientRect();
			const documentWindowPosition = document
				.querySelector(`#${docgenFullScreenEditFlowId} .react-pdf__Document > div`)
				?.getBoundingClientRect();

			if (
				documentWindowPosition?.top! < Math.abs(annotationPosition.top) &&
				documentWindowPosition?.bottom! > Math.abs(annotationPosition.bottom)
			) {
				textAreaRef.current.focus();
			}
		}
	}, [textAnnotationZIndex]);

	React.useEffect(() => {
		if (componentValue.current) {
			const colsAvailable = Math.max(
				...(component.value?.split('\n').map(str => str.length) || [])
			);
			setTotalCols(colsAvailable);
			setTotalRows(component.value?.split('\n').length || 1);
		}
	}, [component.value]);

	return (
		<div
			ref={containerRef}
			style={{
				position: 'absolute',
				top: '1px',
				left: '1px',
				zIndex: textAnnotationZIndex,
			}}
		>
			<StyledTextareaAnnotation
				isFocused={isEditable}
				cols={totalCols}
				rows={totalRows}
				style={{
					position: 'absolute',
					fontSize: Number(component.fontSize),
					fontFamily: 'Courier, sans-serif',
					maxWidth: `${availableWidth - PAGE_WIDTH_THRESHOLD}px`,
					top: 0,
					left: 0,
					flexGrow: 1,
					resize: 'none',
					overflowX: 'auto',
					height: 'auto',
					width: 'auto',
				}}
				value={component.value}
				onChange={e => onUpdate(e)}
				onBlur={handleBlur}
				readOnly={!isEditable}
				autoFocus={isEditable}
				ref={textAreaRef}
			></StyledTextareaAnnotation>
		</div>
	);
};

export default TextAnnotationComponent;
